import { FC, useCallback, useContext } from 'react';
import { useForm } from 'react-hook-form';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Textarea,
  useToast,
} from '@chakra-ui/react';
import { useMutation } from '@apollo/client';
import {
  CreateCommentMutation,
  CreateCommentMutationVariables,
} from '../../generated/types';
import Link from 'next/link';
import { CREATE_COMMENT_QUERY } from '../../queries';
import EntityContext from '../../context/EntityContext';
import getAppConfig from '../../utils/getAppConfig';
import { useDropzone } from 'react-dropzone';
import { CloseIcon } from '@chakra-ui/icons';
import { Rating } from 'react-simple-star-rating';

interface CommentFormProps {
  onSubmit?: () => void;
  parentCommentId?: number;
  isAnswer?: boolean;
}

interface CommentFormData {
  name: string;
  username: string;
  email: string;
  comment: string;
  privacy: boolean;
  subject: string;
  _bp: string;
  imageCopyrights?: boolean;
  images: File[];
  stars?: number;
  vehicle?: string;
  dateVisit?: string;
  closures?: string;
  tipps?: string;
}

export const CommentForm: FC<CommentFormProps> = ({
  onSubmit: onParentSubmit,
  parentCommentId,
  isAnswer,
}) => {
  const entityContext = useContext(EntityContext);
  const toast = useToast();
  const [createComment] = useMutation<
    CreateCommentMutation,
    CreateCommentMutationVariables
  >(CREATE_COMMENT_QUERY);

  const {
    register,
    setValue,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CommentFormData>();

  const images = watch('images');

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length) {
        setValue('images', [...(images || []), acceptedFiles[0]]);
      }
    },
    [images]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/png': ['.png'],
      'image/jpg': ['.jpg', '.jpeg'],
      'image/jpeg': ['.jpeg', '.jpg'],
    },
  });

  const onSubmit = async (values: CommentFormData) => {
    const appConfig = getAppConfig();
    const { data, errors } = await createComment({
      variables: {
        data: {
          ...values,
          entityId: entityContext.entity.id || 0,
          frontend: appConfig.config.frontendName,
          parentCommentId,
        },
      },
    });

    let teamSuffix = '';

    if (appConfig.config.frontendId === 'bikerbetten') {
      teamSuffix = ' Dein Bikerbetten Team.';
    } else if (appConfig.config.frontendId === 'ebikeatlas') {
      teamSuffix = ' Dein eBikeAtlas Team.';
    }

    if (data?.createComment?.created) {
      toast({
        title: 'Kommentar eingereicht.',
        description: `Vielen Dank für Deinen Kommentar und Deine Mithilfe. Unsere Redaktion wird den Kommentar in den nächsten Tagen zum Artikel veröffentlichen.${teamSuffix}`,
        status: 'success',
        duration: 9000,
        isClosable: true,
      });

      reset();
      onParentSubmit && onParentSubmit();
    } else {
      toast({
        title: 'Fehler.',
        description:
          'Es ist ein Fehler aufgetreten. Bitte versuche es später erneut.',
        status: 'error',
        duration: 9000,
        isClosable: true,
      });
    }
  };

  return (
    <>
      {!isAnswer && (
        <>
          <Box fontSize="lg" mb={1} fontWeight="bold">
            Willkommen! Deine Meinung und Erfahrung sind uns wichtig.
          </Box>
          <Box mb={3}>
            Die Beiträge und Tips unserer Community helfen bei der Auswahl von
            Touren, Pässen und Pisten und bieten wertvolle Informationen zum
            aktuellen Zustand und eventuellen Herausforderungen. Nutze die
            Kommentarfunktion, um Hinweise zu Touren, Pässen und Pisten zu
            geben, kurze Erfahrungsberichte zu teilen, oder zusätzliche Details
            bereitzustellen. Auch Deine Fotos der Befahrung sind herzlich
            willkommen!
          </Box>
        </>
      )}
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormControl isInvalid={!!errors.name} isRequired>
          <FormLabel htmlFor="name">Name (wird nicht veröffentlicht)</FormLabel>
          <Input
            variant="outlineDefault"
            bg="white"
            id="name"
            placeholder="Name"
            {...register('name', {
              required: true,
            })}
          />
          <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
        </FormControl>

        <FormControl mt={4} isInvalid={!!errors.username} isRequired>
          <FormLabel htmlFor="username">Veröffentlichter Name</FormLabel>
          <Input
            variant="outlineDefault"
            bg="white"
            id="username"
            placeholder="Veröffentlichter Name"
            {...register('username', {
              required: true,
            })}
          />
          <FormErrorMessage>{errors.username?.message}</FormErrorMessage>
        </FormControl>

        <FormControl mt={4} isInvalid={!!errors.email} isRequired>
          <FormLabel htmlFor="email">
            E-Mail (wird nicht veröffentlicht)
          </FormLabel>
          <Input
            variant="outlineDefault"
            bg="white"
            id="email"
            type="email"
            placeholder="E-Mail"
            {...register('email', {
              required: true,
            })}
          />
          <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
        </FormControl>

        {!isAnswer && (
          <FormControl mt={4} isInvalid={!!errors.dateVisit} isRequired>
            <FormLabel htmlFor="dateVisit">Zeitpunkt der Befahrung</FormLabel>
            <Input
              variant="outlineDefault"
              bg="white"
              id="dateVisit"
              placeholder="Zeitpunkt der Befahrung"
              {...register('dateVisit', {
                required: false,
              })}
            />
            <FormErrorMessage>{errors.dateVisit?.message}</FormErrorMessage>
          </FormControl>
        )}

        {!isAnswer && (
          <FormControl
            mt={4}
            isInvalid={!!errors.vehicle}
            isRequired={!isAnswer}
          >
            <FormLabel htmlFor="vehicle">Befahren mit</FormLabel>
            <Input
              variant="outlineDefault"
              bg="white"
              id="vehicle"
              placeholder="Befahren mit"
              {...register('vehicle', {
                required: false,
              })}
            />
            <FormErrorMessage>{errors.vehicle?.message}</FormErrorMessage>
          </FormControl>
        )}

        {!isAnswer && (
          <FormControl mt={4} isInvalid={!!errors.stars} isRequired>
            <FormLabel htmlFor="stars">Sterne</FormLabel>
            <Box
              flex={1}
              __css={{
                '.star-svg': {
                  display: 'unset',
                },
              }}
            >
              <Rating
                initialValue={0}
                size={30}
                onClick={(value) => {
                  setValue('stars', value);
                }}
              />
            </Box>
            <FormErrorMessage>{errors.stars?.message}</FormErrorMessage>
          </FormControl>
        )}

        <FormControl mt={4} isInvalid={!!errors.comment} isRequired>
          <FormLabel htmlFor="comment">Persönlicher Kommentar</FormLabel>
          <Textarea
            bg="white"
            rows={5}
            id="comment"
            {...register('comment', {
              required: true,
            })}
          />
          <FormErrorMessage>{errors.comment?.message}</FormErrorMessage>
        </FormControl>

        {!isAnswer && (
          <FormControl mt={4} isInvalid={!!errors.closures}>
            <FormLabel htmlFor="closures">Sperrungen</FormLabel>
            <Textarea
              bg="white"
              rows={5}
              id="closures"
              {...register('closures', {
                required: false,
              })}
            />
            <FormErrorMessage>{errors.closures?.message}</FormErrorMessage>
          </FormControl>
        )}

        {!isAnswer && (
          <>
            <FormControl mt={4} isInvalid={!!errors.tipps}>
              <FormLabel htmlFor="tipps">Tipps & Hinweise</FormLabel>
              <Textarea
                bg="white"
                rows={5}
                id="tipps"
                {...register('tipps', {
                  required: false,
                })}
              />
              <FormErrorMessage>{errors.tipps?.message}</FormErrorMessage>
            </FormControl>

            <FormControl mt={4} isInvalid={!!errors.images}>
              <FormLabel htmlFor="comment">Bild</FormLabel>
              {images?.length > 0 && (
                <Box fontWeight="bold" mb={2}>
                  Anhänge:
                  <HStack spacing={2}>
                    {images.map((image, i) => (
                      <Flex
                        key={i}
                        border="1px solid"
                        borderColor="gray.400"
                        bg="gray.100"
                        alignItems="center"
                        justifyContent="space-between"
                        borderRadius="md"
                        py={1}
                        px={2}
                        fontWeight="initial"
                      >
                        {image.name}
                        <Button
                          ml={2}
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setValue(
                              'images',
                              images.filter((img) => img.name !== image.name)
                            );
                          }}
                        >
                          <CloseIcon width={3} />
                        </Button>
                      </Flex>
                    ))}
                  </HStack>
                </Box>
              )}
              <Box
                cursor="pointer"
                border="1px solid"
                borderRadius="md"
                borderColor="gray.400"
                bg={isDragActive ? 'gray.600' : 'gray.100'}
                py={4}
                textAlign="center"
                _hover={{
                  bg: 'gray.200',
                  borderColor: 'gray.500',
                }}
                {...getRootProps()}
              >
                <input {...getInputProps()} />
                Ziehe Dein Bild hierhin oder klicke um Bilder auszuwählen.
                <Box color="gray.600" fontSize="xs" mt={1}>
                  Unterstützt werden nur Bilder (jpg, png).
                </Box>
                <Box color="gray.600" fontWeight="bold" fontSize="xs" mt={1}>
                  Bilder werden auf der Seite in den Kommentaren mit angezeigt.
                </Box>
              </Box>
              <FormErrorMessage>{errors.images?.message}</FormErrorMessage>
            </FormControl>

            {images?.length > 0 && (
              <Checkbox
                mt={4}
                {...register('imageCopyrights', {
                  required: true,
                })}
              >
                Ich bestätige, dass die Bilder mir gehören und ich die
                notwendigen Rechte habe die Bilder zu veröffentlichen.
              </Checkbox>
            )}
          </>
        )}

        <FormControl mt={2} isInvalid={!!errors.privacy}>
          <Checkbox
            {...register('privacy', {
              required: true,
            })}
          >
            Ich habe die{' '}
            <Link href="/impressum-datenschutz" passHref>
              <Box fontWeight="bold" as="a" textDecoration="underline">
                Datenschutzerklärung
              </Box>
            </Link>{' '}
            gelesen und akzeptiere sie.
          </Checkbox>
          {errors.privacy && (
            <FormErrorMessage>
              Du musst die Datenschutzerklärung akzeptieren
            </FormErrorMessage>
          )}
        </FormControl>

        <Input id="_bp" type="hidden" placeholder="BP" {...register('_bp')} />

        <Flex justifyContent="flex-end" mt={6}>
          <Button width="100%" isLoading={isSubmitting} type="submit">
            Kommentar einreichen
          </Button>
        </Flex>

        <Box mt={2} fontSize="sm">
          <Box as="span" fontWeight="bold">
            Bitte beachte:
          </Box>{' '}
          Unsere Redaktion prüft Kommentare vor der Freigabe. Kommerzielle,
          beleidigende oder nicht themenrelevante Beiträge werden nicht
          veröffentlicht!
        </Box>
      </form>
    </>
  );
};
